/** @jsxImportSource @emotion/react */
import { MetaList } from '@/Client/Widgets/MetaList';
import { PlusIcon } from '@kaiizen/kai-shiki/react';

import type { FocusedStripStyles } from './focusedStripStyles';
import { VIEW_ALL_AND_MANAGE_LABEL } from './focusedStripTypes';
import type { FocusedStripCountMode } from './focusedStripTypes';

type FocusedStripHeaderProps = {
  title?: string;
  countMode: FocusedStripCountMode;
  photoCount: number;
  countParts: readonly string[];
  actionLabel?: string;
  styles: FocusedStripStyles;
  onAction?: () => void;
  onOpenPhotoManager?: () => void;
};

export const FocusedStripHeader = ({
  title,
  countMode,
  photoCount,
  countParts,
  actionLabel,
  styles,
  onAction,
  onOpenPhotoManager,
}: FocusedStripHeaderProps) => {
  if (!title && !actionLabel && !onOpenPhotoManager) return null;

  const showPhotoOnlyCount = countMode === 'photos-only' && photoCount > 0;
  const showMediaCount = countMode === 'media' && countParts.length > 0;

  return (
    <div css={styles.header}>
      {title
        ? (
          <span css={styles.title}>
            {title}
            {showPhotoOnlyCount
              ? (
                <span css={styles.countSeparator} data-testid={'focused-photo-count'}>
                  {` • ${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`}
                </span>
              )
              : null}
            {showMediaCount
              ? (
                <>
                  <span css={styles.countSeparator}>{' · '}</span>
                  <MetaList parts={[...countParts]} styles={styles.countMetaList} />
                </>
              )
              : null}
          </span>
        )
        : <span />}
      {actionLabel || onOpenPhotoManager
        ? (
          <span css={styles.headerActions}>
            {actionLabel
              ? (
                <button
                  type={'button'}
                  css={styles.addAction}
                  onClick={onAction}
                  title={actionLabel}
                  aria-label={actionLabel}
                >
                  <PlusIcon size={14} color={'currentColor'} aria-hidden={true} />
                </button>
              )
              : null}
            {onOpenPhotoManager
              ? (
                <button
                  type={'button'}
                  css={styles.manageAction}
                  onClick={onOpenPhotoManager}
                  title={'Open the media manager for this scope'}
                >
                  {VIEW_ALL_AND_MANAGE_LABEL}
                </button>
              )
              : null}
          </span>
        )
        : null}
    </div>
  );
};
